import React, { useEffect, useState } from 'react';
import { AlertTriangle, Camera, Check, Clock, Loader2, Pencil, Save, ScanLine, Send, UploadCloud } from 'lucide-react';
import { toast } from 'sonner';
import { MobileHeader } from './MobileHeader';
import { A4Frame } from '../notice/A4Frame';
import { ScannedNotice } from '../notice/ScannedNotice';
import { officerOcrFields } from '../../data/officer';
import type { OfficerField, OfficerScreen } from '../../types/officer';
import { toBn } from '../../utils/bangla';
const LOW = 90;
export function DocumentPreview({
  go,
  rotation,
  enhanced
}: {
  go: (s: OfficerScreen) => void;
  rotation: number;
  enhanced: boolean;
}) {
  const [uploading, setUploading] = useState(false);
  const [uploaded, setUploaded] = useState(false);
  const upload = (then?: () => void) => {
    setUploading(true);
    setTimeout(() => {
      setUploading(false);
      setUploaded(true);
      toast.success('নোটিশ আপলোড সম্পন্ন', {
        description: 'MOB-CAP-2025-0614-118'
      });
      then?.();
    }, 900);
  };
  return <>
      <MobileHeader title="ডকুমেন্ট প্রিভিউ" onBack={() => go('camera')} data-id="element-426" data-mp="2711f53d76iqh" />
      <div className="flex-1 overflow-y-auto bg-[#F4F7FB] p-4" data-id="element-427" data-mp="2b13hzfuo5i7c">
        <div className="overflow-hidden rounded-xl bg-slate-200 p-3" data-id="element-428" data-mp="1x2c5fxvgg4lb">
          <div style={{
          transform: `rotate(${rotation}deg)`,
          filter: enhanced ? 'contrast(1.2) brightness(1.05)' : undefined
        }} className="transition-transform" data-id="element-429" data-mp="1x4ga4i16h7mn">
            <A4Frame data-id="element-430" data-mp="1nbhjyvq1990x"><ScannedNotice data-id="element-431" data-mp="1nbr1rw0xc9vj" /></A4Frame>
          </div>
        </div>
        <div className="mt-3 rounded-xl bg-white p-3 text-[12.5px] text-slate-600" data-id="element-432" data-mp="1s3slg30f92vl">
          <div className="flex justify-between" data-id="element-433" data-mp="1ebuf1zy3hl34"><span data-id="element-434" data-mp="1ecx74b75ltkm">ফাইল</span><span className="font-mono" data-id="element-435" data-mp="1edfcpyfphk57">IMG_20250614_1004.jpg</span></div>
          <div className="mt-1 flex justify-between" data-id="element-436" data-mp="1ja5ucq75snsq"><span data-id="element-437" data-mp="1jb77d07qjkd3">মান</span><span className="font-medium text-[#16834A]" data-id="element-438" data-mp="1j8kvwphq4kpl">ভালো • ৩০০ DPI সমতুল্য</span></div>
          <div className="mt-1 flex justify-between" data-id="element-439" data-mp="14frf90qptyjc"><span data-id="element-440" data-mp="19daxebqjavgk">অবস্থা</span><span className={uploaded ? 'font-medium text-[#16834A]' : ''} data-id="element-441" data-mp="19bq2um47zcip">{uploaded ? 'আপলোডকৃত' : 'স্থানীয়ভাবে সংরক্ষিত (এনক্রিপ্টেড)'}</span></div>
        </div>
      </div>
      <div className="grid shrink-0 gap-2 border-t border-border bg-white p-3" data-id="element-442" data-mp="lgwe9o60ycoi">
        <div className="grid grid-cols-2 gap-2" data-id="element-443" data-mp="gicrjljlwwv3">
          <button onClick={() => go('camera')} className="flex h-11 items-center justify-center gap-1.5 rounded-xl border border-border text-sm font-medium" data-id="element-444" data-mp="gj4op0sur2nh"><Camera className="h-4 w-4" data-id="element-445" data-mp="1970fxpznk2cv" /> পুনরায় ছবি তুলুন</button>
          <button onClick={() => upload()} disabled={uploading || uploaded} className="flex h-11 items-center justify-center gap-1.5 rounded-xl border border-[#003B71] text-sm font-medium text-[#003B71] disabled:opacity-50" data-id="element-446" data-mp="1968wfdjbxv2y">
            {uploading ? <Loader2 className="h-4 w-4 animate-spin" data-id="element-447" data-mp="u9zziimwvttp" /> : uploaded ? <Check className="h-4 w-4" data-id="element-448" data-mp="u985vdcvk662" /> : <UploadCloud className="h-4 w-4" data-id="element-449" data-mp="3jz1yv9r0kalt" />} আপলোড করুন
          </button>
        </div>
        <button onClick={() => uploaded ? go('ocr') : upload(() => go('ocr'))} disabled={uploading} className="flex h-12 items-center justify-center gap-2 rounded-xl bg-[#003B71] font-semibold text-white" data-id="element-450" data-mp="3jwp7k5zd8nw3">
          <ScanLine className="h-5 w-5" data-id="element-451" data-mp="1zh0v6asxt9bk" /> AI/OCR শুরু করুন
        </button>
      </div>
    </>;
}
export function OcrResult({
  go
}: {
  go: (s: OfficerScreen) => void;
}) {
  const [processing, setProcessing] = useState(true);
  const [fields, setFields] = useState<OfficerField[]>(officerOcrFields);
  const [editing, setEditing] = useState(false);
  const [edited, setEdited] = useState<string[]>([]);
  useEffect(() => {
    const t = setTimeout(() => setProcessing(false), 2000);
    return () => clearTimeout(t);
  }, []);
  const low = fields.filter(f => f.confidence < LOW && !edited.includes(f.key)).length;
  if (processing) {
    return <>
        <MobileHeader title="AI/OCR তথ্য সংগ্রহ" data-id="element-452" data-mp="2s56k79r0xkh7" />
        <div className="flex flex-1 flex-col items-center justify-center bg-white p-8 text-center" data-id="element-453" data-mp="st4140mbas42">
          <div className="relative flex h-24 w-24 items-center justify-center rounded-full bg-[#E8F1FA]" data-id="element-454" data-mp="xr5imh43ajdf">
            <ScanLine className="h-10 w-10 text-[#1769AA]" data-id="element-455" data-mp="2o7zp9nstfh3o" />
            <Loader2 className="absolute inset-0 h-24 w-24 animate-spin text-[#1769AA]/40" strokeWidth={1} data-id="element-456" data-mp="2oalv9j025ojs" />
          </div>
          <div className="mt-5 font-semibold text-[#003B71]" data-id="element-457" data-mp="2x97ab46bmlft">তথ্য সংগ্রহ করা হচ্ছে…</div>
          <div className="mt-1 text-sm text-slate-500" data-id="element-458" data-mp="2t7vs3jps12hn">ডকুমেন্ট বিশ্লেষণ • লেখা শনাক্তকরণ • নির্ভুলতা মূল্যায়ন</div>
        </div>
      </>;
  }
  return <>
      <MobileHeader title="OCR ফলাফল" onBack={() => go('preview')} right={<span className="rounded-full bg-white/15 px-2 py-0.5 text-[11px]" data-id="element-460" data-mp="20k9dlgf0gdg0">গড় ৯৩%</span>} data-id="element-459" data-mp="25ijabvgdvm3q" />
      <div className="flex-1 overflow-y-auto bg-[#F4F7FB] p-4" data-id="element-461" data-mp="2eauy19k44vg0">
        {low > 0 && <div className="mb-3 flex items-start gap-2 rounded-xl border border-[#E69A20]/40 bg-[#FDF3E2] p-3 text-[12.5px] text-[#8a5a06]" data-id="element-462" data-mp="29jcy8ottt0wm">
            <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0" data-id="element-463" data-mp="1lsqzxp9miuzt" /> {toBn(low)}টি অনিশ্চিত মান — মূল নোটিশের সাথে মিলিয়ে নিন
          </div>}
        <div className="space-y-2.5" data-id="element-464" data-mp="17p1um3850269">
          {fields.map(f => {
          const uncertain = f.confidence < LOW && !edited.includes(f.key);
          return <label key={f.key} className={`block rounded-xl border bg-white p-3 ${uncertain ? 'border-[#E69A20] bg-[#FFF8EC]' : 'border-transparent'}`} data-id="element-465" data-mp="1chl52rbj2h6u">
                <span className="flex items-center justify-between text-[12px] text-slate-500" data-id="element-466" data-mp="12qqkrshudro1">
                  <span className="flex items-center gap-1" data-id="element-467" data-mp="1qgb78ld7f722">{f.label}{uncertain && <AlertTriangle className="h-3 w-3 text-[#E69A20]" data-id="element-468" data-mp="1vecqp7ejl8e2" />}</span>
                  <span className={`font-semibold ${uncertain ? 'text-[#E69A20]' : edited.includes(f.key) ? 'text-[#1769AA]' : 'text-[#16834A]'}`} data-id="element-469" data-mp="1vc9x6de6kx4l">{edited.includes(f.key) ? 'সংশোধিত' : `${toBn(f.confidence)}%`}</span>
                </span>
                <input value={f.value} readOnly={!editing} onChange={e => {
              const v = e.target.value;
              setFields(fs => fs.map(x => x.key === f.key ? {
                ...x,
                value: v
              } : x));
              setEdited(ed => ed.includes(f.key) ? ed : [...ed, f.key]);
            }} className={`mt-1 w-full bg-transparent text-[15px] font-semibold outline-none ${f.key === 'caseNo' ? 'font-mono text-[13.5px]' : ''} ${editing ? 'rounded-md border border-input px-2 py-1.5' : ''}`} data-id="element-470" data-mp="51f2mum6nr8c" />
              </label>;
        })}
        </div>
      </div>
      <div className="grid shrink-0 gap-2 border-t border-border bg-white p-3" data-id="element-471" data-mp="1oychxvfa82wi">
        <div className="grid grid-cols-2 gap-2" data-id="element-472" data-mp="1p0gtmzjfa67c">
          <button onClick={() => setEditing(e => !e)} className={`flex h-11 items-center justify-center gap-1.5 rounded-xl border text-sm font-medium ${editing ? 'border-[#1769AA] bg-[#E8F1FA] text-[#1769AA]' : 'border-border'}`} data-id="element-473" data-mp="1k02ktf9zys89">
            {editing ? <Check className="h-4 w-4" data-id="element-474" data-mp="3eaof3r9i1ol6" /> : <Pencil className="h-4 w-4" data-id="element-475" data-mp="39dfvrncm7eic" />} {editing ? 'সম্পন্ন' : 'তথ্য সংশোধন'}
          </button>
          <button onClick={() => toast.success('খসড়া সংরক্ষিত হয়েছে')} className="flex h-11 items-center justify-center gap-1.5 rounded-xl border border-border text-sm font-medium" data-id="element-476" data-mp="3gy08xgekgkyi"><Save className="h-4 w-4" data-id="element-477" data-mp="3lu74cf9c6e27" /> খসড়া সংরক্ষণ</button>
        </div>
        <button onClick={() => {
        toast.success('যাচাইয়ের জন্য পাঠানো হয়েছে');
        go('status');
      }} className="flex h-12 items-center justify-center gap-2 rounded-xl bg-[#16834A] font-semibold text-white" data-id="element-478" data-mp="3qo1vg3r9tvbb"><Send className="h-5 w-5" data-id="element-479" data-mp="2y05qkbrjsx3g" /> যাচাইয়ের জন্য পাঠান</button>
      </div>
    </>;
}
const statusSteps = [{
  label: 'আপলোড সম্পন্ন',
  at: '১৪/০৬/২০২৫ ১০:০৪'
}, {
  label: 'OCR সম্পন্ন',
  at: '১৪/০৬/২০২৫ ১০:০৫'
}, {
  label: 'যাচাই অপেক্ষমাণ',
  at: 'এসআই নাসরিন আক্তারের কাছে'
}, {
  label: 'অনুমোদিত',
  at: ''
}, {
  label: 'QR নোটিশ তৈরি',
  at: ''
}];
export function SubmissionStatus({
  go
}: {
  go: (s: OfficerScreen) => void;
}) {
  const current = 2;
  return <>
      <MobileHeader title="জমার অবস্থা" onBack={() => go('home')} data-id="element-480" data-mp="s4l8mmshixp0" />
      <div className="flex-1 overflow-y-auto bg-[#F4F7FB] p-4" data-id="element-481" data-mp="1d9f7o7i0jsmm">
        <div className="rounded-2xl bg-white p-4 text-center" data-id="element-482" data-mp="1d7m0mr3hn6e3">
          <span className="mx-auto flex h-14 w-14 items-center justify-center rounded-full bg-[#E7F4EC]" data-id="element-483" data-mp="18bn4uiftym3n"><Check className="h-8 w-8 text-[#16834A]" data-id="element-484" data-mp="1n0jntajfdjj1" /></span>
          <div className="mt-2 font-semibold text-[#16834A]" data-id="element-485" data-mp="1i8ru4r4z2xoo">সফলভাবে জমা হয়েছে</div>
          <div className="mt-1 font-mono text-[12px] text-slate-500" data-id="element-486" data-mp="1i6oxvpd888bi">DMP/AI/2025/0614/0748</div>
        </div>
        <ol className="mt-4 rounded-2xl bg-white p-4" data-id="element-487" data-mp="1w20xob68ooza">
          {statusSteps.map((s, i) => {
          const done = i < current;
          const active = i === current;
          return <li key={s.label} className="relative flex gap-3 pb-5 last:pb-0" data-id="element-488" data-mp="25vzx32exhunj" data-mpi={i}>
                {i < statusSteps.length - 1 && <span className={`absolute left-[13px] top-7 h-[calc(100%-20px)] w-0.5 ${done ? 'bg-[#16834A]' : 'bg-slate-200'}`} data-id="element-489" data-mp="bk32x1p3qaos" />}
                <span className={`z-10 flex h-7 w-7 shrink-0 items-center justify-center rounded-full ${done ? 'bg-[#16834A] text-white' : active ? 'bg-[#FDF3E2] text-[#E69A20] ring-2 ring-[#E69A20]' : 'bg-slate-100 text-slate-400'}`} data-id="element-490" data-mp="6m9yoe7czqr5">
                  {done ? <Check className="h-4 w-4" data-id="element-491" data-mp="2n9j2qofhzbsl" /> : active ? <Clock className="h-3.5 w-3.5" data-id="element-492" data-mp="28jk4ymruu2dn" /> : <span className="text-[11px]" data-id="element-493" data-mp="28iii7l1h7rg3">{toBn(i + 1)}</span>}
                </span>
                <div data-id="element-494" data-mp="2dgakn8zqyvyj">
                  <div className={`text-[14px] font-semibold ${done || active ? 'text-slate-900' : 'text-slate-400'}`} data-id="element-495" data-mp="2dfsffmv3x7eh">{s.label}</div>
                  {s.at && <div className="text-[12px] text-slate-500" data-id="element-496" data-mp="24hgt3x19wvru">{s.at}</div>}
                </div>
              </li>;
        })}
        </ol>
      </div>
      <div className="grid shrink-0 grid-cols-2 gap-2 border-t border-border bg-white p-3" data-id="element-497" data-mp="1pmakr3epgkvc">
        <button onClick={() => go('details')} className="h-11 rounded-xl border border-border text-sm font-medium" data-id="element-498" data-mp="1ul3qg9nr37ky">বিস্তারিত দেখুন</button>
        <button onClick={() => go('camera')} className="h-11 rounded-xl bg-[#003B71] text-sm font-semibold text-white" data-id="element-499" data-mp="3oxk2jr389fxq">নতুন স্ক্যান</button>
      </div>
    </>;
}